import React from 'react';

import { FlexBox, InlineList, Orientation, Size, SkeletonTypography, Text, Typography } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~ Private Layout Component ~~~~~~~~~~~~~~~~~~~~~~~~~~ */

interface GroupInfoSectionProps {
    title: string;
    children: React.ReactNode;
}

/**
 * A private, reusable layout component for displaying a titled section.
 * It is not exported and only used by the other components in this file.
 */
const GroupInfoSection: React.FC<GroupInfoSectionProps> = ({ title, children }) => (
    <FlexBox orientation={Orientation.vertical} gap={Size.regular}>
        <Text as="h2" typography={Typography.subtitle1}>
            {title}
        </Text>

        <InlineList>{children}</InlineList>
    </FlexBox>
);

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~ Skeleton Component ~~~~~~~~~~~~~~~~~~~~~~~~~~ */

/**
 * A skeleton component to indicate loading state for the GroupUserSizePopover.
 */
export const GroupUserSizePopoverSkeleton: React.FC = () => {
    const { translateKey } = useTranslate();

    return (
        <GroupInfoSection title={translateKey(GLOBAL.SIZE)}>
            <SkeletonTypography typography={Typography.body1} width="40px" />
        </GroupInfoSection>
    );
};

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~ Main Component ~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export interface GroupUserPopoverProps {
    /**
     * The number of users in the group.
     */
    userCount: number | undefined;
}

/**
 * A component designed to display group size (user count)
 * typically for use within a popover.
 *
 * @param {GroupUserPopoverProps} props The component props.
 * @returns {React.ReactElement | null} The rendered component or null.
 */
export const GroupUserSizePopover: React.FC<GroupUserPopoverProps> = ({ userCount }) => {
    const { translateKey, pluralize, translateAndReplace } = useTranslate();

    if (userCount === undefined) {
        return null;
    }

    return (
        <GroupInfoSection title={translateKey(GLOBAL.SIZE)}>
            <Text as="p" typography={Typography.body1}>
                {translateAndReplace(pluralize(GLOBAL.NB_PEOPLE, userCount), {
                    nb: userCount,
                })}
            </Text>
        </GroupInfoSection>
    );
};
